'use client';
import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import dynamic from 'next/dynamic';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

const TestimonialCard = ({ data, isActive }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isStarted, setIsStarted] = useState(false);

  React.useEffect(() => {
    if (!isActive && isPlaying) {
      setIsPlaying(false);
    }
  }, [isActive, isPlaying]);

  const {
    client_name,
    client_avatar,
    client_profession,
    company_size,
    use_cases,
    head_quarters,
    industries,
    verify_by_upwork,
    client_testimonial,
    video_url,
    image,
    company_logo,
    project_name,
    project_url,
  } = data || {};

  const getYouTubeVideoId = (url) => {
    const videoIdRegex =
      /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/videos\?=(?:.*&)?v=)|\/youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url?.match(videoIdRegex);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeVideoId(video_url);

  const hasReview = !!client_testimonial;
  const isVerified = !!verify_by_upwork;
  const hasLogo = !!company_logo;
  const hasContent = hasReview || isVerified || hasLogo;
  const hasVideo = !!video_url && !!videoId;
  const posterImage = image
    ? image.startsWith('http') || image.startsWith('/')
      ? image
      : `${process.env.NEXT_PUBLIC_API_URL}/assets/${image}`
    : null;

  const infoItems = [
    { label: 'Company size', value: company_size },
    { label: 'Use cases', value: use_cases },
    { label: 'Headquarters', value: head_quarters },
    { label: 'Industries', value: industries },
  ].filter((item) => item.value);

  return (
    <div className="flex flex-col md:flex-row gap-6 w-full lg:min-h-[400px] h-full">
      <div
        className={cn(
          'w-full md:w-[63.5%] relative rounded-2xl overflow-hidden bg-[linear-gradient(to_bottom,rgba(255,255,255,0),rgba(7,17,18,17),rgba(255,255,255,0))] bg-center border border-[#0C3438] h-fit sm:flex-1 sm:h-auto min-h-[300px] grid grid-cols-1 content-center lg:content-stretch gap-4 p-8 transition-all duration-300',
          hasVideo && hasContent
            ? 'lg:grid-cols-[calc(55/100*100%-1rem)]'
            : 'lg:grid-cols-1'
        )}
      >
        <div
          className={cn(
            'contents lg:flex lg:flex-col lg:h-full lg:order-1 py-2',
            !hasReview ? 'lg:justify-center' : 'lg:justify-between'
          )}
        >
          {isVerified && (
            <div
              className={cn(
                'order-1 w-full flex items-center justify-center h-fit lg:h-auto',
                !hasVideo && 'lg:col-span-2',
                !hasReview && hasVideo ? 'lg:flex-1' : ''
              )}
            >
              <Image
                src="/verified-by-upwork.png"
                alt="Verified by Upwork"
                width={200}
                height={80}
                loading="lazy"
                className={cn(
                  'h-auto min-w-[144px] w-1/3 sm:w-1/3 lg:w-auto',
                  hasReview ? 'lg:max-w-[180px]' : 'lg:max-w-[230px]'
                )}
              />
            </div>
          )}

          {hasReview && (
            <div
              className={cn(
                'order-3 flex-grow flex items-center justify-center py-2',
                !hasVideo && 'lg:col-span-2'
              )}
            >
              <p className="text-[#B9B9B9] text-center text-sm lg:text-base font-inter-tight leading-relaxed sm:leading-relaxed lg:leading-relaxed">
                {client_testimonial}
              </p>
            </div>
          )}

          {hasLogo && (
            <div
              className={cn(
                'order-4 w-full flex items-center justify-center h-fit lg:h-auto',
                !hasVideo && 'lg:col-span-2',
                !hasReview && hasVideo ? 'lg:flex-1' : ''
              )}
            >
              <div className="w-full flex items-center justify-center">
                <Image
                  src={
                    company_logo.startsWith('http') ||
                    company_logo.startsWith('/')
                      ? company_logo
                      : `${process.env.NEXT_PUBLIC_API_URL}/assets/${company_logo}`
                  }
                  alt="Company Logo"
                  width={200}
                  height={80}
                  loading="lazy"
                  className={cn(
                    'h-auto max-w-[60px] lg:w-auto',
                    hasReview
                      ? 'min-w-[40px] lg:max-w-[150px]'
                      : ' lg:max-w-[200px]'
                  )}
                />
              </div>
            </div>
          )}
        </div>

        {hasVideo && (
          <div
            className={cn(
              'aspect-video lg:aspect-video order-2 lg:col-start-2 relative rounded-xl overflow-hidden bg-black/40 flex items-center justify-center w-full sm:min-h-[200px] self-center',
              !hasContent && 'lg:col-start-1'
            )}
          >
            {/* Video Controls Overlay */}
            {hasVideo && (
              <div
                onClick={() => {
                  if (!isStarted) setIsStarted(true);
                  setIsPlaying(!isPlaying);
                }}
                className={cn(
                  'absolute inset-0 z-50 p-4 flex transition-opacity duration-300 cursor-pointer',
                  !isStarted
                    ? 'items-center justify-center'
                    : 'justify-start items-end',
                  isStarted && isPlaying
                    ? 'opacity-0 hover:opacity-100'
                    : 'opacity-100'
                )}
              >
                <button className="transform hover:scale-110 transition-transform duration-300 bg-transparent pointer-events-none">
                  {isPlaying ? (
                    <Image
                      width={64}
                      height={64}
                      className="w-7 h-7 md:w-10 md:h-10"
                      src="/assets/client/pause.svg"
                      alt="pause"
                      loading="lazy"
                    />
                  ) : (
                    <Image
                      width={64}
                      height={64}
                      className="w-7 h-7 md:w-10 md:h-10"
                      src="/assets/client/play.svg"
                      alt="play"
                      loading="lazy"
                    />
                  )}
                </button>
              </div>
            )}

            {!isStarted ? (
              posterImage ? (
                <Image
                  src={posterImage}
                  alt={`${client_name ?? 'Client'} testimonial video`}
                  fill
                  sizes="(min-width: 1024px) 420px, 100vw"
                  className="object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-black/40" />
              )
            ) : (
              <ReactPlayer
                url={`https://www.youtube.com/watch?v=${videoId}`}
                className="absolute inset-0 object-cover z-10"
                width="100%"
                height="100%"
                playing={isPlaying}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                controls={false}
                playIcon={<></>}
                pauseIcon={<></>}
              />
            )}
          </div>
        )}
      </div>

      <div
        className="w-full h-fit md:h-full md:w-[36.5%] bg-[#020C0D] bg-[linear-gradient(to_bottom,rgba(6,34,37,0.7),rgba(5,32,34,0.4))]
      bg-center border border-[#0C3438] rounded-2xl p-8 flex flex-col gap-5"
      >
        <div className="flex items-start gap-4">
          <div className="relative w-14 h-14 rounded-full overflow-hidden border-2 border-[#1b9ca81f] shrink-0">
            {client_avatar && (
              <Image
                src={`${process.env.NEXT_PUBLIC_API_URL}/assets/${client_avatar}`}
                alt={client_name}
                fill
                className="object-cover"
                loading="lazy"
              />
            )}
          </div>
          <div className="font-inter-tight">
            <h3 className="text-white text-lg font-semibold">{client_name}</h3>
            <p className="text-gray-400 text-sm leading-tight mt-1">
              {client_profession}
            </p>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-2 gap-y-8 gap-x-4">
          {infoItems.map((item, index) => (
            <div
              key={item.label}
              className={cn(
                'col-span-1',
                infoItems.length === 3 &&
                  index === 2 &&
                  'col-span-2 flex justify-center',
                infoItems.length === 1 && 'col-span-2 flex justify-center'
              )}
            >
              <MetaItem label={item.label} value={item.value} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MetaItem = ({ label, value }) => (
  <div className="flex flex-col items-center text-center pt-2.5 pb-1.5 font-inter-tight">
    <div className="mb-2 text-[#466575]">
      <MetaIcon label={label} />
    </div>
    <span className="text-[#32C5D6] text-sm font-medium mb-1">{label}</span>
    <span className="text-gray-400 text-xs">{value}</span>
  </div>
);

const MetaIcon = ({ label }) => {
  if (label === 'Company size')
    return (
      <Image
        src="/icons/company-size.svg"
        width={45}
        height={45}
        alt="Company size"
        className="w-8 h-6 sm:w-11 sm:h-8"
        loading="lazy"
      />
    );
  if (label === 'Use cases')
    return (
      <Image
        src="/icons/use-cases.svg"
        width={45}
        height={45}
        alt="Use cases"
        loading="lazy"
        className="w-7 h-7 sm:w-9 sm:h-9 aspect-square"
      />
    );
  if (label === 'Headquarters')
    return (
      <Image
        src="/icons/headquarters.svg"
        width={45}
        height={45}
        alt="Headquarters"
        loading="lazy"
        className="w-7 h-7 sm:w-9 sm:h-9 aspect-square"
      />
    );
  if (label === 'Industries')
    return (
      <Image
        src="/icons/industries.svg"
        width={45}
        height={45}
        alt="Industries"
        loading="lazy"
        className="w-7 h-7 sm:w-9 sm:h-9 aspect-square"
      />
    );

  return null;
};

export default TestimonialCard;
